import { useCallback, useMemo, useState } from 'react';
import { appShortcuts } from 'src/constants/appShortcuts';
import { Notification } from 'src/graphql/entities/notification';
import { useRegisterShortcuts } from 'src/utils/hooks';
import { useNotificationMethods } from './use-notification-menu-items';

export const useInboxArchive = ({
  notifications,
  isRowSelected,
  navigateToTarget,
}: {
  notifications: Notification[];
  isRowSelected: (notification: Notification) => boolean;
  navigateToTarget: (notification: Notification) => void;
}) => {
  const { archive } = useNotificationMethods();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const openNextAfter = useCallback(
    (current: Notification) => {
      const idx = notifications.findIndex((n) => n.id === current.id);
      const next = notifications[idx + 1] || notifications[idx - 1];
      if (next) {
        navigateToTarget(next);
      }
    },
    [notifications, navigateToTarget],
  );

  const openNextAfterIfSelected = useCallback(
    (notification: Notification) =>
      isRowSelected(notification) ? openNextAfter(notification) : undefined,
    [isRowSelected, openNextAfter],
  );

  const archiveAt = useCallback(
    (idx: number) => {
      const target = notifications[idx];
      if (!target) return;
      archive(target, () => openNextAfterIfSelected(target));

      const newLastIdx = notifications.length - 2;
      if (newLastIdx < 0) {
        setActiveIndex(null);
      } else {
        setActiveIndex(Math.min(idx, newLastIdx));
      }
    },
    [archive, notifications, openNextAfterIfSelected],
  );

  useRegisterShortcuts(
    useMemo(
      () => ({
        ...appShortcuts.inboxArchiveNotification,
        callback: () => {
          if (activeIndex !== null) {
            archiveAt(activeIndex);
          }
        },
      }),
      [activeIndex, archiveAt],
    ),
  );

  return { activeIndex, setActiveIndex, archiveAt, openNextAfterIfSelected };
};
